import List from 'rc-virtual-list';
import { ColumnSchema } from '@client/new/components/StandardTable/StandardTable';
import {
  F0,
  TypedValue,
  ValueOf,
  toTypedValueConstructor,
} from '@client/new/helpers/types';
import {
  Typography,
  TypographyProps,
} from '@design-system/components/typography/typography';
import { Checkbox, CircularProgress } from '@mui/material';
import React, { PropsWithChildren, useEffect, useState } from 'react';
import {
  CellCTAItems,
  CellCTAProps,
  CellMenu,
  MenuItems,
} from '@client/new/components/TableShared/Cell';
import { cell, row } from './TableList.css';
import { clsx } from 'clsx';
import { sprinkles } from '@design-system/sprinkles.css';
import { useBreakpoints } from '@design-system/breakpointsStore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareCheck } from '@fortawesome/pro-solid-svg-icons';
import { TableLoader } from '@client/new/components/TableShared/TableLoader';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Tag as DsTag } from '@design-system/components/tag/tag';

export type TableListCell =
  | TypedValue<
      'header',
      {
        imageUrl: string;
        title: string;
        subtitle: string;
        onClick?: F0;
        tag?: {
          icon: IconProp;
          label: string;
          className?: string;
        };
      }
    >
  | TypedValue<'tag', string>
  | TypedValue<'text', { text: string; className?: string } & TypographyProps>
  | TypedValue<'custom', JSX.Element>
  | TypedValue<'actions', Array<CellCTAProps>>
  | TypedValue<'menu', MenuItems>;

export type TableListCellProcessing = TypedValue<
  'header',
  { imageUrl?: string; title: string; subtitle?: string }
>;

export type TableListRowProcessing = {
  id: string;
  cells: TableListCellProcessing[];
};

export type TableListRow = {
  id: string;
  cells: TableListCell[];
};

const Tag = (p: PropsWithChildren) => (
  <div
    style={{
      background: '#eee',
      borderRadius: '30px',
      padding: '4px 16px',
      textAlign: 'center',
    }}
  >
    <Typography variant="body" size="medium" as="span">
      {p.children}
    </Typography>
  </div>
);

const Header = (p: ValueOf<TableListCell, 'header'>) => (
  <div className="flex flex-row w-full" onClick={p.onClick}>
    <div className="mr-4 w-[68px] h-[68px] p-2 flex justify-center items-center">
      <img
        src={p.imageUrl}
        className={clsx(
          sprinkles({ clipPath: 'sm' }),
          'max-w-[52px] max-h-[52px]',
        )}
      />
    </div>
    <div className="flex justify-between items-center w-full">
      <div className="flex flex-col justify-center">
        <Typography
          variant="body"
          size="large"
          color="N-100"
          className={clsx(
            p.tag &&
              'md:max-w-[170px] lg:max-w-[250px] overflow-hidden text-ellipsis whitespace-nowrap',
          )}
        >
          {p.title}
        </Typography>
        <Typography variant="body" size="medium" color="N-60">
          {p.subtitle}
        </Typography>
      </div>
      {!p.tag ? null : (
        <div className="flex items-center">
          <DsTag
            label={p.tag.label}
            startIcon={p.tag.icon}
            className={p.tag.className}
          />
        </div>
      )}
    </div>
  </div>
);

const HeaderProcessing = (p: ValueOf<TableListCellProcessing, 'header'>) => (
  <div style={{ display: 'flex', flexDirection: 'row' }}>
    <div className="w-[68px] h-[68px] py-2 px-3 flex justify-center items-center mr-4">
      <div className="w-[34px] h-[52px] bg-black rounded flex justify-center items-center">
        <CircularProgress className="w-[20px] h-[20px] text-white" />
      </div>
    </div>
    <div className="flex flex-col justify-center">
      <div>
        <Typography variant="body" size="large" color="N-100">
          {p.title}
        </Typography>
      </div>
      <div>
        <Typography variant="body" size="medium" color="N-60">
          {p.subtitle}
        </Typography>
      </div>
    </div>
  </div>
);
const cellRenderer = (p: TableListCell): JSX.Element => {
  switch (p.type) {
    case 'header':
      return <Header {...p.value} />;
    case 'tag':
      return <Tag>{p.value}</Tag>;
    case 'text': {
      const { text, className, ...props } = p.value;
      return (
        <Typography
          size="medium"
          variant="body"
          {...props}
          className={className}
        >
          {text}
        </Typography>
      );
    }
    case 'actions':
      return <CellCTAItems items={p.value} />;
    case 'menu':
      return <CellMenu items={p.value} />;
    case 'custom':
      return p.value;
  }
};

const Cell = ({
  data,
  schema,
}: {
  data: TableListCell;
  schema: ColumnSchema;
}): JSX.Element => {
  return (
    <div className={clsx(cell, sprinkles({ textAlign: schema.justify }))}>
      {cellRenderer(data)}
    </div>
  );
};

const CellProcessing = ({
  data,
  schema,
}: {
  data: TableListCellProcessing;
  schema: ColumnSchema;
}): JSX.Element => {
  return (
    <div
      className={clsx(cell, sprinkles({ textAlign: schema.justify || 'left' }))}
    >
      <HeaderProcessing {...data.value} />
    </div>
  );
};

export const toCell = toTypedValueConstructor<TableListCell>();

export const toCellProcessing =
  toTypedValueConstructor<TableListCellProcessing>();

const toCSSSize = (v?: number | string, def = '100%') =>
  v ? (typeof v === 'string' ? v : v + 'px') : def;

export const toGridTemplateColumns = (v: Array<ColumnSchema>) =>
  v
    .map((e) =>
      e.width === 'stretchable'
        ? 'auto'
        : e.maxWidth
        ? `minmax(${toCSSSize(e.width)}, ${toCSSSize(e.maxWidth)})`
        : toCSSSize(e.width),
    )
    .join(' ');

const Row = (p: {
  cells: TableListCell[];
  schema: Array<ColumnSchema>;
  style?: React.CSSProperties;
  itemHeight?: string | number;
  itemGap?: string | number;
  rowClassName?: string;
}) => (
  <div
    className={clsx(row, p?.rowClassName)}
    style={{
      marginBottom: toCSSSize(p.itemGap),
      gridTemplateColumns: toGridTemplateColumns(p.schema),
      height: toCSSSize(p.itemHeight),
      ...p.style,
    }}
  >
    {p.cells.map((v, i) => (
      <Cell data={v} key={i} schema={p.schema[i]} />
    ))}
  </div>
);

export const RowProcessing = (p: {
  cells: TableListCellProcessing[];
  schema: Array<ColumnSchema>;
  style?: React.CSSProperties;
  itemHeight?: string | number;
  itemGap?: string | number;
}) => (
  <div
    className={clsx(row, 'h-[84px] mb-4')}
    style={{
      gridTemplateColumns: toGridTemplateColumns(p.schema),
      ...p.style,
    }}
  >
    {p.cells.map((v, i) => (
      <CellProcessing data={v} key={i} schema={p.schema[i]} />
    ))}
  </div>
);

export const TableList = (p: {
  rows: TableListRow[];
  schema: Array<ColumnSchema>;
  itemHeight: number;
  itemGap: number;
  hideHeader?: boolean;
  listHeight?: number;
  rowClassName?: string;
  withSelection?: {
    onSelect: (value: string) => void;
    selectedIds: string[];
  };
  loading?: boolean;
}) => {
  const [h, setH] = useState(0);
  const isMobile = useBreakpoints((s) => s.mobile);
  const updateSize = () => {
    const nh = window.innerHeight - (isMobile ? 250 : 300);
    setH(nh - (nh % 100));
  };
  useEffect(() => {
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  if (p?.loading) {
    return <TableLoader rows={5} height={130} />;
  }

  return (
    <div className="overscroll-contain">
      {p.hideHeader ? null : (
        <div
          style={{
            display: 'grid',
            padding: '0 16px',
            gridTemplateColumns: toGridTemplateColumns(p.schema),
          }}
        >
          {p.schema.map((v, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: v.justify,
              }}
            >
              <Typography variant="body" size="small" color="N-100">
                {v.label}
              </Typography>
            </div>
          ))}
        </div>
      )}
      <List
        data={p.rows}
        itemHeight={p.itemHeight + p.itemGap}
        itemKey="id"
        height={h}
        className={clsx(p?.rowClassName)}
      >
        {(v) => (
          <div className="flex items-center">
            {!p?.withSelection ? null : (
              <Checkbox
                checkedIcon={
                  <FontAwesomeIcon icon={faSquareCheck} className="w-6 h-6" />
                }
                checked={p.withSelection.selectedIds.includes(v.id)}
                onChange={() => p.withSelection?.onSelect(v.id)}
              />
            )}
            <Row
              key={v.id}
              cells={v.cells}
              schema={p.schema}
              itemHeight={p.itemHeight}
              itemGap={p.itemGap}
              rowClassName={p?.rowClassName}
            />
          </div>
        )}
      </List>
    </div>
  );
};
